import { useEffect, useRef, useState } from "react";
import { useHoldDigitizingBanner } from "../digitizingServiceStorage";
import { usePostHog } from "posthog-js/react";
import { getStoredEmail, setStoredEmail } from "../emailStorage";

export type ProUpsellVariant = "price_19" | "price_29";

// Display prices only -- the "price_19"/"price_29" keys are the PostHog
// flag's own variant names and stay as-is; this map is the one place that
// decides what price each variant actually shows.
const VARIANT_PRICE: Record<ProUpsellVariant, number> = {
  price_19: 5,
  price_29: 29,
};

// Exactly these three, in this order -- not user-editable copy, per the
// fake-door test's fixed reason set.
const REASON_CHIPS = ["Icons", "Commercial license", "Better editing tools"] as const;

interface ProUpsellCardProps {
  variant: ProUpsellVariant;
  onDone: () => void;
  // Called the moment "I'm interested" is clicked, separately from onDone:
  // the caller uses this to permanently stop showing the card for this
  // person going forward, which has to apply even if they never reach
  // "confirmed" (close the tab, decline to leave an email, etc).
  onInterested?: () => void;
}

type Step = "card" | "reasons" | "email" | "confirmed";

/**
 * A fake-door demand test, not a product: "I'm interested" and a captured
 * email are the entire signal this collects. No checkout, no charge, no
 * fulfillment -- see the flag's own description in PostHog for the ramp
 * plan this is gated behind.
 *
 * True dead center with a backdrop, same as EmailDesignModal -- front and
 * center rather than tucked in a corner, a demand signal this quiet to
 * notice is a demand signal that under-counts. Safe to give it a real
 * backdrop (which blocks interaction with the page behind it) because
 * CreatePage never mounts this while the design-email modal is still open
 * (see its own render condition): the two would both want the same screen
 * space if shown at once, and on a common laptop viewport (1366x768 among
 * others) a stacked or offset placement still measurably overlapped the
 * modal's controls no matter how this was sized. Sequencing them -- the
 * utility first, this once it's dealt with -- resolved that without
 * touching EmailDesignModal at all, and meant this could pick up a real
 * backdrop once there was no longer a second overlay it would have to
 * coexist with.
 */
export function ProUpsellCard({ variant, onDone, onInterested }: ProUpsellCardProps) {
  // The digitizing service banner waits while this is on screen.
  useHoldDigitizingBanner();
  const posthog = usePostHog();
  const price = VARIANT_PRICE[variant];
  const [step, setStep] = useState<Step>("card");
  const [reasons, setReasons] = useState<string[]>([]);
  // Prefilled from whichever capture -- this one or the design-email one --
  // has already collected an address this session, so the user is never
  // asked to type it twice. Safe to read once at mount: this never mounts
  // until the design-email modal has already closed (see the comment
  // above), so whatever it wrote to shared storage is already there.
  const initialEmail = useRef(getStoredEmail()).current;
  const [email, setEmail] = useState(initialEmail);
  const emailWasPrefilled = Boolean(initialEmail);

  const eventBase = { variant, price };
  const capture = (event: string, props: object) => posthog?.capture(event, props as any);

  // Fires pro_prompt_dismissed for any way out of the flow before it's
  // actually completed -- "No thanks," the backdrop, a future close
  // affordance -- but not from the confirmed step, which already has its
  // own completion event (pro_email_submitted) and isn't a dismissal.
  const handleClose = () => {
    if (step !== "confirmed") {
      capture("pro_prompt_dismissed", eventBase);
    }
    onDone();
  };

  // Same guard EmailDesignModal uses: a double-click on the download button
  // that triggered this delivers two click events at the same screen
  // position. The first opens the card; the backdrop is a fixed,
  // full-viewport overlay, so the second would land on it and close the
  // card immediately, reading as "clicked download, the offer flashed and
  // vanished."
  const openedAtRef = useRef(Date.now());
  const handleBackdropClick = () => {
    if (Date.now() - openedAtRef.current < 300) return;
    handleClose();
  };

  // Guards against React StrictMode's dev-mode double-invoke of effects,
  // which would otherwise double-fire pro_prompt_shown for a single render
  // of the card the frequency cap already decided to show exactly once.
  const shownFired = useRef(false);
  useEffect(() => {
    if (shownFired.current) return;
    shownFired.current = true;
    capture("pro_prompt_shown", { ...eventBase, trigger: "post_download" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInterested = () => {
    capture("pro_prompt_clicked", eventBase);
    onInterested?.();
    setStep("reasons");
  };

  const toggleReason = (reason: string) => {
    setReasons((current) =>
      current.includes(reason) ? current.filter((r) => r !== reason) : [...current, reason],
    );
  };

  const handleContinue = () => {
    capture("pro_reason_selected", { ...eventBase, reasons });
    setStep("email");
  };

  const handleConfirm = () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    setStoredEmail(trimmed);
    // The app already identifies every visitor by their authorized-link
    // token as soon as it resolves (see App.tsx) -- well before a download
    // (and so this card) can happen -- so there is always already a
    // person. $set attaches the address to that same person rather than
    // calling identify() again with the email, which would attempt to
    // merge two person profiles instead of just adding a property to the
    // one that already exists. Same pattern email_capture_submitted uses.
    capture("pro_email_submitted", {
      ...eventBase,
      reasons,
      email_prefilled: emailWasPrefilled,
      $set: { email: trimmed },
    });
    setStep("confirmed");
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
        zIndex: 1001,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Pro early access"
        style={{
          background: "white",
          borderRadius: "8px",
          padding: "1.5rem",
          width: "22rem",
          maxWidth: "100%",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
        }}
      >
      {step === "card" && (
        <>
          <h3 style={{ marginTop: 0, marginBottom: "0.5rem", fontSize: "1.05rem" }}>
            Loved that? Do more with Pro
          </h3>
          <p style={{ color: "#555", fontSize: "0.9rem", marginBottom: "1rem" }}>
            Unlock icons for your designs, plus a commercial-use license -- ${price}/mo.
          </p>
          <div style={{ display: "flex", gap: "0.5rem" }}>
            <button onClick={handleInterested} style={primaryButton}>
              I'm interested
            </button>
            <button onClick={handleClose} style={secondaryButton}>
              No thanks
            </button>
          </div>
        </>
      )}

      {step === "reasons" && (
        <>
          <h3 style={{ marginTop: 0, marginBottom: "0.3rem", fontSize: "0.95rem" }}>
            What would you use Pro for?
          </h3>
          <p style={{ color: "#555", fontSize: "0.78rem", marginBottom: "0.5rem" }}>
            Pick anything that applies.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.65rem" }}>
            {REASON_CHIPS.map((reason) => {
              const active = reasons.includes(reason);
              return (
                <button
                  key={reason}
                  type="button"
                  onClick={() => toggleReason(reason)}
                  aria-pressed={active}
                  style={active ? chipActive : chip}
                >
                  {reason}
                </button>
              );
            })}
          </div>
          <button onClick={handleContinue} style={primaryButton}>
            Continue
          </button>
        </>
      )}

      {step === "email" && (
        <>
          <h3 style={{ marginTop: 0, marginBottom: "0.3rem", fontSize: "0.95rem" }}>
            Get early access
          </h3>
          {emailWasPrefilled ? (
            <p style={{ color: "#555", fontSize: "0.82rem", marginBottom: "0.6rem" }}>
              We'll email you at <strong>{email}</strong> for early access.
            </p>
          ) : (
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleConfirm()}
              placeholder="you@example.com"
              autoFocus
              style={{
                width: "100%",
                padding: "0.45rem 0.6rem",
                fontSize: "0.9rem",
                border: "1px solid #ccc",
                borderRadius: "4px",
                marginBottom: "0.6rem",
                boxSizing: "border-box",
              }}
            />
          )}
          <button onClick={handleConfirm} disabled={!email.trim()} style={primaryButton}>
            Confirm
          </button>
        </>
      )}

      {step === "confirmed" && (
        <>
          <h3 style={{ marginTop: 0, marginBottom: "0.3rem", fontSize: "0.95rem" }}>
            You're on the early-access list 🎉
          </h3>
          <p style={{ color: "#555", fontSize: "0.82rem", marginBottom: "0.6rem" }}>
            Pro is coming soon -- we'll email you the moment it's ready.
          </p>
          <button onClick={handleClose} style={secondaryButton}>
            Close
          </button>
        </>
      )}
      </div>
    </div>
  );
}

const primaryButton: React.CSSProperties = {
  flex: 1,
  padding: "0.4rem 0.8rem",
  fontSize: "0.85rem",
  border: "none",
  borderRadius: "4px",
  background: "#333",
  color: "white",
  cursor: "pointer",
};

const secondaryButton: React.CSSProperties = {
  padding: "0.4rem 0.8rem",
  fontSize: "0.85rem",
  border: "1px solid #ccc",
  borderRadius: "4px",
  background: "white",
  color: "#333",
  cursor: "pointer",
};

const chip: React.CSSProperties = {
  padding: "0.3rem 0.6rem",
  fontSize: "0.78rem",
  border: "1px solid #ccc",
  borderRadius: "999px",
  background: "white",
  color: "#333",
  cursor: "pointer",
};

const chipActive: React.CSSProperties = {
  ...chip,
  background: "#333",
  color: "white",
  borderColor: "#333",
};
